import { useEffect, useRef, useMemo } from 'react'

import { usePubSub, pubSubEventHandler } from '@services'
import { customToast, PUB_SUB_EVENT } from '@common'
import { useOrderEventContext } from '@context'

export const usePubSubEvents = (options = {}) => {
    const { currentOrderId } = useOrderEventContext()
    const recentToasts = useRef({})
    const handlersRef = useRef({})

    const {
        events = [PUB_SUB_EVENT.ORDER_PAYMENT_STATUS_UPDATE, PUB_SUB_EVENT.COMPLETED_ORDER, PUB_SUB_EVENT.COMPLETED_DISH],
        handlers = {},
        showToasts = true,
        autoConnect = true,
    } = options

    // Keep handlers ref updated to avoid re-registering event listeners when handlers object reference changes
    useEffect(() => {
        handlersRef.current = handlers
    }, [handlers])

    const { isConnected, connect, disconnect } = usePubSub()

    // Add a function to prevent duplicate toast display
    const showToastOnce = (key, message) => {
        const now = Date.now()
        // If this key's toast has already been shown in the last 2 seconds, do not show it again
        if (recentToasts.current[key] && now - recentToasts.current[key] < 2000) {
            return
        }

        // Show toast and record time
        customToast.success(message)
        recentToasts.current[key] = now
    }

    const defaultHandlers = useMemo(
        () => ({
            [PUB_SUB_EVENT.ORDER_PAYMENT_STATUS_UPDATE]: order => {
                // Only show toast if currentOrderId is set and matches the order
                if (showToasts && currentOrderId && currentOrderId === order.orderId) {
                    const toastKey = `paid_${order.orderId}_${order.number}`
                    showToastOnce(toastKey, `Order #${order.number || order.orderId} paid!`)
                }
                return order
            },
            [PUB_SUB_EVENT.COMPLETED_ORDER]: order => {
                if (showToasts && currentOrderId && currentOrderId === order.orderId) {
                    const toastKey = `completed_${order.orderId}_${order.number}`
                    showToastOnce(toastKey, `Order #${order.number || order.orderId} completed!`)
                }
                return order
            },
            [PUB_SUB_EVENT.COMPLETED_DISH]: order => {
                if (showToasts && currentOrderId && currentOrderId === order.orderId) {
                    const toastKey = `completed_dish_${order.orderId}_${order.number}`
                    showToastOnce(toastKey, `Dish completed!`)
                }
                return order
            },
        }),
        [showToasts, currentOrderId]
    )

    // Connect when mounted (use autoConnect to control)
    useEffect(() => {
        if (autoConnect && !isConnected) connect()
        return () => {
            // disconnect()
        }
    }, [autoConnect, isConnected, connect])

    // Set event listeners
    useEffect(() => {
        if (!isConnected) return
        const removers = []
        events.forEach(type => {
            // combined handler that first runs the default handler and then the custom handler
            const createCombinedHandler = (defaultHandler, customHandler) => {
                return data => {
                    // run the default handler first
                    const defaultResult = defaultHandler ? defaultHandler(data) : data

                    // run the custom handler if it exists, and pass the result of the default handler to it
                    if (customHandler) {
                        return customHandler(defaultResult)
                    }

                    return defaultResult
                }
            }

            const defaultHandler = defaultHandlers[type]
            const customHandler = handlersRef.current[type]

            // if there is a default handler or custom handler, create a combined handler
            if (defaultHandler || customHandler) {
                const combinedHandler = createCombinedHandler(defaultHandler, customHandler)
                removers.push(pubSubEventHandler.addEventListener(type, data => combinedHandler(data)))
            }
        })
        return () => {
            removers.forEach(r => r && r())
        }
    }, [isConnected, events, defaultHandlers])

    return { isConnected, isRealTimeEnabled: isConnected, connect, disconnect }
}

export default usePubSubEvents
